'use client'

/**
 * Full-screen splash used for two cases:
 *   1. Auth resolve (ClientLayoutRouter) — no label, animated 1.5s bar.
 *   2. First-ever offline precache / initial PowerSync sync (AppBootGate) —
 *      shows a label and fills the bar from real progress when available.
 *
 * Visual contract (keep both call sites identical): the App Store brand
 * gradient (navy→teal→green, sampled from playstore/backgrounds/gradient_bg),
 * a centered rounded ZeroSnag icon badge, white progress bar at the bottom of
 * the viewport. No background watermark.
 */
export function SplashScreen({
  label,
  progress,
}: {
  label?: string
  progress?: { done: number; total: number } | null
}) {
  const pct = progress
    ? Math.min(100, Math.round((progress.done / Math.max(1, progress.total)) * 100))
    : null

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[100] w-full flex flex-col overflow-hidden touch-none select-none"
      style={{
        height: '100dvh',
        maxHeight: '-webkit-fill-available',
        position: 'fixed',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top right, #011c49 0%, #0c324f 30%, #124a58 55%, #1f7165 100%)',
        }}
      />

      <div className="relative z-10 flex flex-col h-full justify-center">
        <div className="flex-1 flex items-center justify-center py-8">
          <img
            src="/icon-512.png"
            alt="ZeroSnag"
            className="w-[44%] max-w-[216px] h-auto rounded-[20%]"
            style={{ boxShadow: '0 20px 45px rgba(0, 0, 0, 0.5)' }}
          />
        </div>

        <div className="pb-8 px-8" style={{ marginTop: 'auto' }}>
          {label && (
            <div className="text-center text-white/90 text-sm font-medium mb-3 drop-shadow">
              {label}
            </div>
          )}
          <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden shadow-2xl backdrop-blur-sm">
            <div
              className="h-full bg-white rounded-full shadow-lg"
              style={
                pct !== null
                  ? {
                      width: `${pct}%`,
                      transition: 'width 200ms ease-out',
                      boxShadow: '0 0 20px rgba(255, 255, 255, 0.8)',
                    }
                  : {
                      width: '0%',
                      animation: 'splashLoadingBar 1.5s ease-out forwards',
                      boxShadow: '0 0 20px rgba(255, 255, 255, 0.8)',
                    }
              }
            />
          </div>
        </div>
        <style>{`
          @keyframes splashLoadingBar {
            0% {
              width: 0%;
            }
            100% {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  )
}
